// src/store/slices/sponsorSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sponsorAuthAPI, sponsorEventsAPI } from "@/services/sponsorApi";
import { setSponsorAccessToken, clearSponsorAccessToken } from "@/services/sponsorApi";

// ==========================================
// AUTH THUNKS
// ==========================================

export const loginSponsor = createAsyncThunk(
    "sponsor/login",
    async (credentials, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.login(credentials);
            if (response.data?.token) {
                setSponsorAccessToken(response.data.token);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Login failed" });
        }
    }
);

export const applySponsor = createAsyncThunk(
    "sponsor/apply",
    async (applicationData, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.apply(applicationData);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Application failed" });
        }
    }
);

export const fetchSponsorProfile = createAsyncThunk(
    "sponsor/fetchProfile",
    async (_, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.getMe();
            return response.data?.sponsor || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch profile" });
        }
    }
);

/**
 * Silent refresh for sponsor - attempt to restore session on page load
 */
export const silentRefreshSponsor = createAsyncThunk(
    "sponsor/silentRefresh",
    async (_, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.refresh();
            const token = response.data?.data?.token;
            if (token) {
                setSponsorAccessToken(token);
                return response.data;
            }
            return rejectWithValue({ message: "No token in refresh response" });
        } catch (err) {
            clearSponsorAccessToken();
            return rejectWithValue(err.response?.data || { message: "Session expired" });
        }
    }
);

export const updateSponsorPassword = createAsyncThunk(
    "sponsor/updatePassword",
    async (passwordData, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.updatePassword(passwordData);
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update password" });
        }
    }
);

export const uploadSponsorLogo = createAsyncThunk(
    "sponsor/uploadLogo",
    async (formData, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.uploadLogo(formData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to upload logo" });
        }
    }
);

export const removeSponsorLogo = createAsyncThunk(
    "sponsor/removeLogo",
    async (_, { rejectWithValue }) => {
        try {
            await sponsorAuthAPI.removeLogo();
            return null;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to remove logo" });
        }
    }
);

// ==========================================
// EVENTS THUNKS
// ==========================================

export const updateSponsorEmail = createAsyncThunk(
    "sponsor/updateEmail",
    async (emailData, { rejectWithValue }) => {
        try {
            const response = await sponsorAuthAPI.updateEmail(emailData);
            if (response.data?.token) {
                setSponsorAccessToken(response.data.token);
            }
            return response;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to update email" });
        }
    }
);

export const fetchSponsorableEvents = createAsyncThunk(
    "sponsor/fetchEvents",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await sponsorEventsAPI.getSponsorableEvents(params);
            return {
                events: response.data?.events || [],
                component_counts: response.data?.component_counts || [],
                pagination: {
                    page: response.data?.pagination?.currentPage || 1,
                    pages: response.data?.pagination?.totalPages || 1,
                    total: response.data?.pagination?.totalDocuments || 0
                }
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch events" });
        }
    }
);

export const fetchSponsorEventDetails = createAsyncThunk(
    "sponsor/fetchEventDetails",
    async (eventId, { rejectWithValue }) => {
        try {
            const response = await sponsorEventsAPI.getEventDetails(eventId);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch event details" });
        }
    }
);

export const submitSponsorshipInterest = createAsyncThunk(
    "sponsor/submitInterest",
    async ({ eventId, data }, { rejectWithValue }) => {
        try {
            const response = await sponsorEventsAPI.submitInterest(eventId, data);
            return response.data?.interest || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to submit interest" });
        }
    }
);

export const editSponsorshipInterest = createAsyncThunk(
    "sponsor/editInterest",
    async ({ eventId, data }, { rejectWithValue }) => {
        try {
            const response = await sponsorEventsAPI.editInterest(eventId, data);
            return response.data?.interest || response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to edit interest" });
        }
    }
);

export const fetchMyInterests = createAsyncThunk(
    "sponsor/fetchInterests",
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await sponsorEventsAPI.getMyInterests(params);
            return {
                interests: response.data?.interests || [],
                component_counts: response.data?.component_counts || [],
                pagination: {
                    page: response.data?.pagination?.currentPage || 1,
                    pages: response.data?.pagination?.totalPages || 1,
                    total: response.data?.pagination?.totalDocuments || 0
                }
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: "Failed to fetch interests" });
        }
    }
);

// ==========================================
// SLICE
// ==========================================

const sponsorSlice = createSlice({
    name: "sponsor",
    initialState: {
        // Auth state
        sponsor: null,
        token: null,
        isAuthenticated: false,

        // Events state
        events: [],
        eventsPagination: { page: 1, pages: 1, total: 0 },
        currentEvent: null,
        existingInterest: null,
        interests: [],
        interestsPagination: { page: 1, pages: 1, total: 0 },
        sponsorComponentCounts: [],

        // Loading/Error state
        isLoading: true, // Start true for silent refresh check
        error: null,
        applicationSubmitted: false,
    },
    reducers: {
        logoutSponsor: (state) => {
            state.sponsor = null;
            state.token = null;
            state.isAuthenticated = false;
            state.events = [];
            state.currentEvent = null;
            state.interests = [];
            clearSponsorAccessToken();
        },
        clearSponsorError: (state) => {
            state.error = null;
        },
        resetApplicationState: (state) => {
            state.applicationSubmitted = false;
        },
    },
    extraReducers: (builder) => {
        builder
            // LOGIN
            .addCase(loginSponsor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginSponsor.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sponsor = action.payload.data?.sponsor || action.payload.data;
                state.token = action.payload.data?.token;
                state.isAuthenticated = true;
            })
            .addCase(loginSponsor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Login failed";
            })

            // APPLY
            .addCase(applySponsor.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(applySponsor.fulfilled, (state) => {
                state.isLoading = false;
                state.applicationSubmitted = true;
            })
            .addCase(applySponsor.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Application failed";
            })

            // FETCH PROFILE
            .addCase(fetchSponsorProfile.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSponsorProfile.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sponsor = action.payload;
                state.isAuthenticated = true;
            })
            .addCase(fetchSponsorProfile.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch profile";
                if (action.payload?.status === 401) {
                    state.sponsor = null;
                    state.token = null;
                    state.isAuthenticated = false;
                    clearSponsorAccessToken();
                }
            })

            // SILENT REFRESH
            .addCase(silentRefreshSponsor.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(silentRefreshSponsor.fulfilled, (state, action) => {
                state.isLoading = false;
                state.token = action.payload?.data?.token;
                state.sponsor = action.payload?.data?.sponsor;
                state.isAuthenticated = true;
            })
            .addCase(silentRefreshSponsor.rejected, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.sponsor = null;
                state.token = null;
            })

            // UPDATE PASSWORD
            .addCase(updateSponsorPassword.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSponsorPassword.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(updateSponsorPassword.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to update password";
            })

            // UPDATE EMAIL
            .addCase(updateSponsorEmail.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateSponsorEmail.fulfilled, (state, action) => {
                state.isLoading = false;
                state.sponsor = action.payload.data?.sponsor || action.payload.data;
                state.token = action.payload.data?.token;
                state.isAuthenticated = true;
            })
            .addCase(updateSponsorEmail.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to update email";
            })

            // UPLOAD LOGO
            .addCase(uploadSponsorLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(uploadSponsorLogo.fulfilled, (state, action) => {
                state.isLoading = false;
                if (state.sponsor && action.payload.sponsor?.logo_url) {
                    state.sponsor.logo_url = action.payload.sponsor.logo_url;
                } else if (action.payload.sponsor) {
                    state.sponsor = action.payload.sponsor;
                }
            })
            .addCase(uploadSponsorLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to upload logo";
            })

            // REMOVE LOGO
            .addCase(removeSponsorLogo.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(removeSponsorLogo.fulfilled, (state) => {
                state.isLoading = false;
                if (state.sponsor) {
                    state.sponsor.logo_url = null;
                }
            })
            .addCase(removeSponsorLogo.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to remove logo";
            })

            // FETCH EVENTS
            .addCase(fetchSponsorableEvents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchSponsorableEvents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.events = action.payload.events;
                state.eventsPagination = action.payload.pagination;
                state.sponsorComponentCounts = action.payload.component_counts || [];
            })
            .addCase(fetchSponsorableEvents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch events";
            })

            // FETCH EVENT DETAILS
            .addCase(fetchSponsorEventDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.existingInterest = null;
            })
            .addCase(fetchSponsorEventDetails.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentEvent = action.payload.event || action.payload;

                // Handle different response structures for existing interest
                if (action.payload.has_submitted_interest && action.payload.submitted_interest) {
                    state.existingInterest = {
                        ...action.payload.submitted_interest,
                        status: action.payload.interest_status || 'pending'
                    };
                } else {
                    state.existingInterest = action.payload.existing_interest || null;
                }
            })
            .addCase(fetchSponsorEventDetails.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch event";
            })

            // SUBMIT INTEREST
            .addCase(submitSponsorshipInterest.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(submitSponsorshipInterest.fulfilled, (state, action) => {
                state.isLoading = false;
                state.existingInterest = action.payload;
            })
            .addCase(submitSponsorshipInterest.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to submit interest";
            })

            // EDIT INTEREST
            .addCase(editSponsorshipInterest.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(editSponsorshipInterest.fulfilled, (state, action) => {
                state.isLoading = false;
                state.existingInterest = action.payload;
            })
            .addCase(editSponsorshipInterest.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to edit interest";
            })

            // FETCH INTERESTS
            .addCase(fetchMyInterests.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMyInterests.fulfilled, (state, action) => {
                state.isLoading = false;
                state.interests = action.payload.interests;
                state.interestsPagination = action.payload.pagination;
                state.sponsorComponentCounts = action.payload.component_counts || [];
            })
            .addCase(fetchMyInterests.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || "Failed to fetch interests";
            });
    },
});

export const { logoutSponsor, clearSponsorError, resetApplicationState } = sponsorSlice.actions;
export default sponsorSlice.reducer;
